import type { ItemTimeslots } from "@simbelapp/order-sdk";
import type { IAttendanceTimeslotOption } from "~/utils/interfaces/attendance-review-interfaces";

/** Said on a slot whose attendance has already been declared — confirming would rewrite it. */
const ALREADY_DECLARED_NOTE = "déjà émargé";

/**
 * The slot a session settles on the admin's behalf: its only one, and only while nothing has been
 * declared on it yet.
 *
 * A sole slot already emargé is deliberately *not* settled. Re-declaring one is a legitimate
 * correction — the session's own declaration screen keeps a "Déclaré" card clickable — but it has
 * to be a deliberate answer: settled silently, the field carrying the "déjà émargé" note never
 * renders, and the admin overwrites an existing attendance with no signal at all.
 */
export function impliedAttendanceTimeslot(timeslots: ItemTimeslots[]): ItemTimeslots | null {
  const [only] = timeslots;

  return timeslots.length === 1 && only?.available ? only : null;
}

/** "3,5 h", the way a duration is written in French. Whole hours keep no decimals. */
export function attendanceDurationLabel(hours: number | null | undefined): string | null {
  if (!Number.isFinite(hours) || (hours as number) <= 0) return null;

  return `${String(hours).replace(".", ",")} h`;
}

/**
 * One slot, named the way the admin has to tell it apart from the others: its own name when it has
 * one, then the day, then the hours it runs. The duration is part of the label on purpose — the
 * sheet's hours are read against the whole session, so the slot's own length is what the admin
 * checks them against before confirming.
 *
 * Formatters are handed in rather than imported: `useFormats` is a composable, and this has to stay
 * callable from a plain derivation check.
 */
export function attendanceTimeslotLabel(
  timeslot: ItemTimeslots,
  formatDate: (date: string | Date) => string,
  formatTime: (time: string | null | undefined) => string,
): string {
  const name = timeslot.name?.trim();
  const start = timeslot.startDate ? formatDate(timeslot.startDate) : "";
  const end = timeslot.endDate ? formatDate(timeslot.endDate) : "";
  // A slot spilling over midnight is dated on both ends; the usual one names its single day once.
  const dates = start && end && start !== end ? `${start} – ${end}` : start || end;

  const startTime = timeslot.startTime ? formatTime(timeslot.startTime) : "";
  const endTime = timeslot.endTime ? formatTime(timeslot.endTime) : "";
  const times = startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime;

  const parts = [name, dates, times, attendanceDurationLabel(timeslot.trainingDurationHours)].filter(Boolean);
  if (!timeslot.available) parts.push(ALREADY_DECLARED_NOTE);

  // A slot the API described with nothing at all still has to be selectable, so it falls back to its id.
  return parts.length ? parts.join(" · ") : timeslot.timeslotId;
}

/** The slots as the footer dropdown lists them, in the order the API returned them. */
export function attendanceTimeslotOptions(
  timeslots: ItemTimeslots[],
  formatDate: (date: string | Date) => string,
  formatTime: (time: string | null | undefined) => string,
): IAttendanceTimeslotOption[] {
  return timeslots.map((timeslot) => ({
    timeslotId: timeslot.timeslotId,
    label: attendanceTimeslotLabel(timeslot, formatDate, formatTime),
  }));
}
